import { Component, OnInit, ViewChild, Input, Output, EventEmitter, OnChanges, SimpleChanges } from '@angular/core';
import { AnalyticsQueryOptions, AnalyticsService, SessionData } from '@shared/services/analytics.service';
import { Course, HubMode, UserType } from '@shared/models';
import { MatSort, MatSortable } from '@angular/material/sort';
import { MatLegacyTableDataSource as MatTableDataSource } from '@angular/material/legacy-table';
import { LoadState } from '@app/main/analytics/loader-directive/loader.module';
import { saveAs } from 'file-saver';
import { toSnakeCase } from '@shared/utils/string.utils';

@Component({
  selector: 'session-list',
  templateUrl: './session-list.component.html',
  styleUrls: ['./session-list.component.scss']
})
export class SessionListComponent implements OnInit, OnChanges {
  @Input() course: Course;
  @Input() date_from: Date;
  @Input() date_to: Date;
  @Input() search: string;
  @Input() programsMode: boolean = false;
  @Output() onExplore = new EventEmitter<{ sessionId: string; userName: string }>();
  @Output() onSearch = new EventEmitter<{ name: string }>();
  @Output() onExport = new EventEmitter<{ sessionId: string; userName: string }>();
  public dataSource: MatTableDataSource<SessionData>;
  public noData = false;
  public loadStatus: string;
  @Input() selectedUserType: UserType;
  @Output() onUserTypeChange = new EventEmitter<UserType>();
  displayedColumns: string[];
  constructor(private _analyticsService: AnalyticsService) {}

  ngOnInit(): void {
    const spaceModeDisplayedColumns = ['date', 'actor_name', 'duration', 'sceneViews', 'completed', 'score', 'multi'];
    const programsModeDisplayedColums = ['date', 'actor_name', 'duration', 'actions'];
    this.displayedColumns = !this.programsMode ? spaceModeDisplayedColumns : programsModeDisplayedColums;
    this.loadSessions();
  }
  ngOnChanges(changes: SimpleChanges): void {
    if (
      (changes.date_from && !changes.date_from.isFirstChange() && changes.date_from.currentValue !== changes.date_from.previousValue) ||
      (changes.date_to && !changes.date_to.isFirstChange() && changes.date_to.currentValue !== changes.date_to.previousValue)
    ) {
      this.loadSessions();
    }
    if (changes.search && changes.search.isFirstChange()) {
      this.loadSessions();
    }
  }

  loadSessions() {
    this.loadStatus = LoadState.loading;
    const queryOptions = { dateFrom: this.date_from, dateTo: this.date_to, userName: this.search };
    this._analyticsService.getSessions(this.course.id, queryOptions, 'json', this.selectedUserType).subscribe(
      (sessions) => {
        this.loadStatus = LoadState.completed;
        if (!sessions || sessions.length === 0) {
          this.noData = true;
          this.dataSource = null;
          return;
        }
        // Get basic course sessions
        this.dataSource = new MatTableDataSource(sessions as SessionData[]);
      },
      () => {
        this.loadStatus = LoadState.error;
      }
    );
  }
  goToDetails(session: any): void {
    this.onExplore.emit({ sessionId: session._id, userName: session.actor_name });
  }
  exportLogs(session: any): void {
    this.onExport.emit({ sessionId: session._id, userName: session.actor_name });
  }

  formatActor(name: string): string {
    if (name.indexOf('Anonymous') > -1) {
      return 'Anonymous';
    }
    return name;
  }
  sortData(event: any): void {
    this.dataSource.data = this.dataSource.data.sort((a: any, b: any) => {
      const direction = event.direction === 'desc' ? -1 : 1;
      if (event.active === 'actor_name') {
        return a['actor_name'].localeCompare(b['actor_name']) * direction;
      }
      let sort = 0;
      if (a[event.active] > b[event.active]) {
        sort = 1;
      } else if (a[event.active] < b[event.active]) {
        sort = -1;
      }
      return sort * direction;
    });
  }
  searchSessions(userName: string) {
    this.onSearch.emit({ name: userName });
    this.search = userName;
    this.loadSessions();
  }
  async exportStats() {
    let csvData;
    const queryOptions: AnalyticsQueryOptions = { dateFrom: this.date_from, dateTo: this.date_to, userName: this.search };
    queryOptions.mode = this.programsMode ? HubMode.program : HubMode.space;
    csvData = await this._analyticsService.getDetailedSessions(this.course.id, queryOptions, 'csv').toPromise();

    const fileName = `Wonda_export_${toSnakeCase(this.course.title)}_detailed_sessions__${new Date().toISOString().split('T')[0]}`;
    const blob = new Blob([csvData] as any, { type: 'text/csv' });
    saveAs(blob, fileName);
  }
  async handleUserTypeChange(value: UserType) {
    this.onUserTypeChange.emit(value);
    await this.loadSessions();
  }
}
